import { requireImportTarget } from "./import-target";
import {
  AssetRecordType,
  createShapeId,
  type TLImageShape,
} from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { Sheet } from "../shared/schema";
import { uploadFile } from "./api";
import { imagePlacement } from "./sheet-operations";
import { pageRange } from "../shared/file-content";
import { PDF_MAX_BYTES } from "../shared/pdf-preview";
import { sourceMeters } from "./source-scale";
async function pageAsset(
  editor: Editor,
  room: string,
  bytes: Uint8Array,
  source: string,
  name: string,
  page: number,
  signal: AbortSignal,
  cloud: boolean,
) {
  const cached = editor
    .getAssets()
    .find(
      (a) =>
        a.type === "image" &&
        a.meta.documentSource === source &&
        a.meta.pdfPage === page,
    );
  if (cached?.type === "image") return cached;
  const { pdfPreview } = await import("./pdf-preview");
  const rendered = await pdfPreview(bytes, page, signal);
  const uploaded = await uploadFile(
    room,
    new File([rendered.blob], `${name}-page-${page}.png`, {
      type: "image/png",
    }),
    signal,
    cloud,
  );
  signal.throwIfAborted();
  if (!uploaded.image) throw new Error("PDF page preview unavailable.");
  const asset = AssetRecordType.create({
    id: AssetRecordType.createId(),
    type: "image",
    props: {
      name,
      src: uploaded.src,
      w: uploaded.image.w,
      h: uploaded.image.h,
      mimeType: "image/png",
      isAnimated: false,
    },
    meta: {
      imagePreviews: true,
      documentSource: source,
      documentName: name,
      pdfPage: page,
      pdfPages: rendered.pages,
      pdfPaperWidthMm: rendered.paperWidthMm,
      documentPreview: `PDF · page ${page} of ${rendered.pages}`,
    },
  });
  if (asset.type !== "image") throw new Error("Invalid PDF preview.");
  return asset;
}
export async function importPdf(
  editor: Editor,
  room: string,
  file: File,
  signal: AbortSignal,
  cloud: boolean,
  page = 1,
  placement = imagePlacement(editor),
  shapeId: import("@tldraw/tlschema").TLShapeId = createShapeId(),
) {
  const source = await uploadFile(
    room,
    new File([file], file.name, { type: "application/pdf" }),
    signal,
    cloud,
  );
  const asset = await pageAsset(
    editor,
    room,
    new Uint8Array(await file.arrayBuffer()),
    source.src,
    file.name,
    page,
    signal,
    cloud,
  );
  signal.throwIfAborted();
  if (editor.getIsReadonly()) throw new Error("Edit access was removed.");
  requireImportTarget(editor, placement);
  if (editor.getShape(shapeId)) return shapeId;
  const w = 520,
    h = (w * asset.props.h) / asset.props.w,
    id = shapeId;
  editor.markHistoryStoppingPoint("Add PDF");
  if (!editor.getAsset(asset.id)) editor.createAssets([asset]);
  editor.createShape<TLImageShape>({
    opacity: 1,
    id,
    type: "image",
    parentId: placement.parentId,
    x: placement.center.x - w / 2,
    y: placement.center.y - h / 2,
    props: { w, h, assetId: asset.id, altText: `${file.name} · page ${page}` },
  });
  editor.select(id);
  editor.zoomToBounds(editor.getShapePageBounds(id)!, { inset: 180 });
  return id;
}
export async function changePdfPage(
  editor: Editor,
  room: string,
  shape: TLImageShape,
  page: number,
  signal: AbortSignal,
  cloud: boolean,
) {
  if (editor.getSortedChildIdsForParent(shape.id).length)
    throw new Error(
      "This page has attached annotations. Extract another page to preserve their alignment.",
    );
  const currentAsset =
    shape.props.assetId && editor.getAsset(shape.props.assetId);
  if (!currentAsset?.meta.pdfPages) throw new Error("Select a PDF document.");
  pageRange(String(page), Number(currentAsset.meta.pdfPages));
  const source = String(currentAsset.meta.documentSource),
    name = String(currentAsset.meta.documentName);
  const bytes = await pdfSourceBytes(source, signal);
  const asset = await pageAsset(
    editor,
    room,
    bytes,
    source,
    name,
    page,
    signal,
    cloud,
  );
  signal.throwIfAborted();
  const current = editor.getShape<TLImageShape>(shape.id);
  if (!current || current.isLocked || editor.getIsReadonly())
    throw new Error("The document is no longer editable.");
  if (
    current.props.assetId !== shape.props.assetId ||
    editor.getSortedChildIdsForParent(current.id).length
  )
    throw new Error("This document changed during loading. Try again.");
  const savedScales =
    current.meta.pdfPageScales && typeof current.meta.pdfPageScales === "object"
      ? {
          ...(current.meta.pdfPageScales as Record<
            string,
            { factor: number; label: string }
          >),
        }
      : {};
  const oldPage = Number(currentAsset.meta.pdfPage);
  savedScales[String(oldPage)] = {
    factor: sourceMeters(editor, current),
    label: String(current.meta.calibration || "Not to Scale"),
  };
  const restored = savedScales[String(page)];
  const factor =
    restored && Number.isFinite(restored.factor) && restored.factor > 0
      ? restored.factor
      : 0;
  editor.markHistoryStoppingPoint("Change PDF page");
  if (!editor.getAsset(asset.id)) editor.createAssets([asset]);
  editor.updateShape<TLImageShape>({
    id: shape.id,
    type: "image",
    props: {
      assetId: asset.id,
      h: (current.props.w * asset.props.h) / asset.props.w,
      crop: null,
      altText: `${name} · page ${page}`,
    },
    meta: {
      ...current.meta,
      pdfPageScales: savedScales,
      metersPerUnit: factor,
      calibrationWidth: current.props.w,
      calibration: factor ? restored.label : "Not to Scale",
      scaleReview: false,
    },
  });
}
export async function pdfSourceBytes(source: string, signal: AbortSignal) {
  if (!/^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(source))
    throw new Error("Invalid document source.");
  const res = await fetch(source, { signal });
  if (!res.ok) throw new Error("Cannot read the original PDF.");
  const length = Number(res.headers.get("content-length"));
  if (length > PDF_MAX_BYTES) throw new Error("PDF exceeds the preview limit.");
  if (!res.body) {
    const bytes = new Uint8Array(await res.arrayBuffer());
    if (bytes.length > PDF_MAX_BYTES)
      throw new Error("PDF exceeds the preview limit.");
    return bytes;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      signal.throwIfAborted();
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > PDF_MAX_BYTES)
        throw new Error("PDF exceeds the preview limit.");
      chunks.push(value);
    }
  } catch (error) {
    void reader.cancel().catch(() => {});
    throw error;
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}
export async function extractPdfPages(
  editor: Editor,
  room: string,
  shape: TLImageShape,
  range: string,
  asSheets: boolean | "combined",
  signal: AbortSignal,
  cloud: boolean,
  progress: (s: string) => void,
) {
  const asset = shape.props.assetId && editor.getAsset(shape.props.assetId);
  if (!asset) throw new Error("Document unavailable.");
  const pages = pageRange(range, Number(asset.meta.pdfPages));
  const source = String(asset.meta.documentSource),
    name = String(asset.meta.documentName);
  const targetPage = editor.getCurrentPageId();
  const bytes = await pdfSourceBytes(source, signal);
  const ids: TLImageShape["id"][] = [];
  // Prepare sequentially without publishing partial pages or holding an undo
  // step open across asynchronous rendering/uploads and unrelated user actions.
  const previews: Awaited<ReturnType<typeof pageAsset>>[] = [];
  for (const [index, page] of pages.entries()) {
    signal.throwIfAborted();
    progress(`Extracting page ${page} (${index + 1}/${pages.length})…`);
    previews.push(
      await pageAsset(editor, room, bytes, source, name, page, signal, cloud),
    );
  }
  signal.throwIfAborted();
  const current = editor.getShape<TLImageShape>(shape.id);
  if (
    !current ||
    editor.getIsReadonly() ||
    editor.isShapeOrAncestorLocked(current) ||
    current.props.assetId !== shape.props.assetId ||
    editor.getCurrentPageId() !== targetPage
  )
    throw new Error(
      "The document or destination changed during extraction. Try again.",
    );
  const bounds = editor.getShapePageBounds(current)!;
  const mark = editor.markHistoryStoppingPoint("Extract PDF pages");
  try {
    editor.run(() => {
      let rowY = bounds.maxY + 100,
        rowHeight = 0;
      for (const [index, page] of pages.entries()) {
        const preview = previews[index];
        if (!editor.getAsset(preview.id)) editor.createAssets([preview]);
        const id = createShapeId(),
          w = asSheets === true ? 1120 : 520,
          h = (w * preview.props.h) / preview.props.w;
        if (index > 0 && index % 4 === 0) {
          rowY += rowHeight + 100;
          rowHeight = 0;
        }
        rowHeight = Math.max(rowHeight, h);
        const x = bounds.x + (index % 4) * (w + 80),
          y = rowY;
        if (asSheets === true)
          editor.createShape<Sheet>({
            opacity: 1,
            id,
            type: "sheet",
            parentId: editor.getCurrentPageId(),
            x,
            y,
            props: {
              w,
              h,
              name: `${name} · ${page}`,
              source,
              preview: preview.props.src!,
              page,
              paper: "Custom",
              metersPerUnit: 0,
              calibration: "Not calibrated",
              revision: "01",
            },
          });
        else
          editor.createShape<TLImageShape>({
            opacity: 1,
            id,
            type: "image",
            parentId: editor.getCurrentPageId(),
            x,
            y,
            props: {
              w,
              h,
              assetId: preview.id,
              altText: `${name} · page ${page}`,
            },
          });
        ids.push(id);
      }
      if (asSheets === "combined" && ids.length) {
        const boxes = ids.map((id) => editor.getShapePageBounds(id)!);
        const left = Math.min(...boxes.map((b) => b.minX)) - 24,
          top = Math.min(...boxes.map((b) => b.minY)) - 24;
        const w = Math.max(...boxes.map((b) => b.maxX)) - left + 24,
          h = Math.max(...boxes.map((b) => b.maxY)) - top + 24;
        const sheetId = createShapeId();
        editor.createShape<Sheet>({
          opacity: 1,
          id: sheetId,
          type: "sheet",
          parentId: editor.getCurrentPageId(),
          x: left,
          y: top,
          props: { w, h, name: `${name} · selected pages`, paper: "Custom" },
          meta: { paperWidthMm: (w * 3) / 8, paperHeightMm: (h * 3) / 8 },
        });
        editor.reparentShapes(ids, sheetId);
        editor.select(sheetId);
      } else editor.select(...ids);
    });
  } catch (error) {
    editor.bailToMark(mark);
    throw error;
  }
  editor.commitHistoryStoppingPoint(mark);
  editor.zoomToBounds(editor.getSelectionPageBounds()!, {
    inset: 180,
    animation: { duration: 200 },
  });
  return ids;
}
